import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from '../components/layout/AppLayout';
import { getNotifications, markNotificationRead, markAllNotificationsRead } from '../services/api';
import { formatDistanceToNow } from 'date-fns';

const TYPE_ICON = {
  info:    '💬',
  warning: '⚠️',
  success: '✅',
  error:   '❌',
  default: '🔔',
};

const TYPE_COLOR = {
  info:    { bg: 'var(--info-bg)',    color: 'var(--info)' },
  warning: { bg: 'var(--warning-bg)', color: 'var(--warning)' },
  success: { bg: 'var(--success-bg)', color: 'var(--success)' },
  error:   { bg: 'var(--danger-bg)',  color: 'var(--danger)' },
};

export default function NotificationsPage() {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [markingAll, setMarkingAll] = useState(false);

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await getNotifications();
      setNotifications(res.data.notifications || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchNotifications(); }, [fetchNotifications]);

  const handleMarkRead = async (n) => {
    if (!n.is_read) {
      try {
        await markNotificationRead(n.id);
        setNotifications(prev =>
          prev.map(item => item.id === n.id ? { ...item, is_read: true } : item)
        );
      } catch (err) {
        console.error(err);
      }
    }
    // Navigate: link field takes priority, then ticket, then nothing
    if (n.link) {
      navigate(n.link);
    } else if (n.related_ticket_id) {
      navigate(`/tickets/${n.related_ticket_id}`);
    }
  };

  const handleMarkAll = async () => {
    setMarkingAll(true);
    try {
      await markAllNotificationsRead();
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    } catch (err) {
      console.error(err);
    } finally {
      setMarkingAll(false);
    }
  };

  const unreadCount = notifications.filter(n => !n.is_read).length;

  return (
    <AppLayout pageTitle="Notifications">
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <h2 style={{ fontSize: 16, fontWeight: 600, color: 'var(--text-main)', margin: 0 }}>
            All Notifications
          </h2>
          {unreadCount > 0 && (
            <span style={{
              background: '#ef4444', color: '#fff',
              borderRadius: 12, padding: '2px 8px',
              fontSize: 11, fontWeight: 700,
            }}>
              {unreadCount} unread
            </span>
          )}
        </div>
        {unreadCount > 0 && (
          <button
            className="btn btn-secondary btn-sm"
            onClick={handleMarkAll}
            disabled={markingAll}
          >
            {markingAll ? 'Marking…' : '✓ Mark all read'}
          </button>
        )}
      </div>

      <div className="card">
        {loading ? (
          <div className="flex-center" style={{ height: 200 }}>
            <div className="spinner" style={{ width: 32, height: 32 }} />
          </div>
        ) : notifications.length === 0 ? (
          <div className="empty-state" style={{ padding: 60 }}>
            <div className="empty-icon">🔔</div>
            <h3 style={{ fontSize: 16, fontWeight: 600, color: 'var(--text-main)', marginBottom: 8 }}>
              No notifications
            </h3>
            <p style={{ color: 'var(--text-muted)' }}>
              You're all caught up! Notifications will appear here.
            </p>
          </div>
        ) : (
          <div>
            {notifications.map((n, i) => {
              const typeStyle = TYPE_COLOR[n.type] || TYPE_COLOR.info;
              const icon = TYPE_ICON[n.type] || TYPE_ICON.default;
              const isLast = i === notifications.length - 1;

              return (
                <div
                  key={n.id}
                  onClick={() => handleMarkRead(n)}
                  style={{
                    display: 'flex', alignItems: 'flex-start', gap: 14,
                    padding: '14px 20px',
                    background: n.is_read ? 'transparent' : 'var(--primary-light)',
                    borderBottom: isLast ? 'none' : '1px solid var(--border-light)',
                    cursor: (n.related_ticket_id || n.link) ? 'pointer' : 'default',
                    transition: 'background 0.15s',
                  }}
                  onMouseEnter={e => {
                    if (n.related_ticket_id || n.link) e.currentTarget.style.background = 'var(--bg-hover)';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.background = n.is_read ? 'transparent' : 'var(--primary-light)';
                  }}
                >
                  {/* Icon */}
                  <div style={{
                    width: 36, height: 36, borderRadius: '50%',
                    background: typeStyle.bg,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 16, flexShrink: 0,
                  }}>
                    {icon}
                  </div>

                  {/* Content */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
                      <span style={{
                        fontSize: 13, fontWeight: n.is_read ? 500 : 700,
                        color: 'var(--text-main)',
                      }}>
                        {n.title}
                      </span>
                      {n.ticket_number && (
                        <span style={{
                          fontSize: 11, color: 'var(--primary)', fontFamily: 'var(--font-mono)',
                          background: 'var(--primary-light)', borderRadius: 4, padding: '1px 6px',
                        }}>
                          {n.ticket_number}
                        </span>
                      )}
                    </div>
                    <p style={{ fontSize: 13, color: 'var(--text-sub)', margin: 0, lineHeight: 1.4 }}>
                      {n.message}
                    </p>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                      {formatDistanceToNow(new Date(n.created_at), { addSuffix: true })}
                    </div>
                  </div>

                  {/* Unread dot */}
                  <div style={{
                    width: 8, height: 8, borderRadius: '50%',
                    background: n.is_read ? 'transparent' : 'var(--primary)',
                    flexShrink: 0, marginTop: 8,
                  }} />
                </div>
              );
            })}
          </div>
        )}
      </div>
    </AppLayout>
  );
}
